if (!customElements.get('product-modal')) {
  const ZOOM_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
  const ZOOM_DURATION_MS = 480;

  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this._zoomTimer = null;
        this._navReady = false;
      }

      connectedCallback() {
        super.connectedCallback();
        this.initNavigation();
        this.initSwipeNavigation();
        this.initOutsideClose();
      }

      initOutsideClose() {
        if (this._outsideCloseReady) return;
        this._outsideCloseReady = true;

        // Content fills the viewport, so "outside the image" still lands on dialog/content.
        // Close when the click is on the backdrop chrome, not on media / controls.
        this.addEventListener('click', (event) => {
          if (!this.hasAttribute('open') || this.isZoomAnimating()) return;
          if (this._suppressOutsideClose) return;
          if (this.shouldIgnoreOutsideClose(event.target)) return;
          if (this.isOutsideMediaClick(event.target)) this.hide();
        });

        // Also handle pointerup from ModalDialog path for mouse users on empty chrome
        this.addEventListener(
          'pointerup',
          (event) => {
            if (!this.hasAttribute('open') || this.isZoomAnimating()) return;
            if (this._suppressOutsideClose) return;
            // Touch/pen close is handled after swipe detection; avoid fighting gestures.
            if (event.pointerType && event.pointerType !== 'mouse') return;
            if (this.shouldIgnoreOutsideClose(event.target)) return;
            if (this.isOutsideMediaClick(event.target)) {
              event.preventDefault();
              this.hide();
            }
          },
          true
        );
      }

      initSwipeNavigation() {
        if (this._swipeReady) return;
        this._swipeReady = true;

        const content = this.querySelector('.product-media-modal__content');
        if (!content) return;

        const SWIPE_THRESHOLD = 48;
        const DIR_RATIO = 1.15;
        let tracking = false;
        let pointerId = null;
        let startX = 0;
        let startY = 0;
        let moved = false;

        const reset = () => {
          tracking = false;
          pointerId = null;
          moved = false;
        };

        const suppressCloseBriefly = () => {
          this._suppressOutsideClose = true;
          clearTimeout(this._suppressCloseTimer);
          this._suppressCloseTimer = setTimeout(() => {
            this._suppressOutsideClose = false;
          }, 280);
        };

        content.addEventListener(
          'pointerdown',
          (event) => {
            if (!this.hasAttribute('open') || this.isZoomAnimating()) return;
            if (event.pointerType === 'mouse' && event.button !== 0) return;
            if (
              event.target.closest(
                'video, iframe, model-viewer, .product-media-modal__toggle, .product-media-modal__nav'
              )
            ) {
              return;
            }

            tracking = true;
            pointerId = event.pointerId;
            startX = event.clientX;
            startY = event.clientY;
            moved = false;

            try {
              content.setPointerCapture(event.pointerId);
            } catch (err) {
              // Some browsers reject capture on non-capturable targets.
            }
          },
          { passive: true }
        );

        content.addEventListener(
          'pointermove',
          (event) => {
            if (!tracking || event.pointerId !== pointerId) return;
            const dx = event.clientX - startX;
            const dy = event.clientY - startY;
            if (Math.abs(dx) > 8 || Math.abs(dy) > 8) moved = true;
          },
          { passive: true }
        );

        const onPointerEnd = (event) => {
          if (!tracking || event.pointerId !== pointerId) return;

          const dx = event.clientX - startX;
          const dy = event.clientY - startY;
          const absX = Math.abs(dx);
          const absY = Math.abs(dy);
          const isHorizontalSwipe = absX >= SWIPE_THRESHOLD && absX > absY * DIR_RATIO;

          if (isHorizontalSwipe) {
            suppressCloseBriefly();
            content.classList.remove(
              'product-media-modal__content--swipe-next',
              'product-media-modal__content--swipe-prev'
            );
            // Force reflow so the direction class can re-trigger animation.
            void content.offsetWidth;
            content.classList.add(
              dx < 0 ? 'product-media-modal__content--swipe-next' : 'product-media-modal__content--swipe-prev'
            );
            this.stepMedia(dx < 0 ? 1 : -1);
            reset();
            return;
          }

          // Touch tap on empty chrome closes (mouse handled separately).
          if (
            event.pointerType !== 'mouse' &&
            !moved &&
            !this.shouldIgnoreOutsideClose(event.target) &&
            this.isOutsideMediaClick(event.target)
          ) {
            this.hide();
          }

          reset();
        };

        content.addEventListener('pointerup', onPointerEnd);
        content.addEventListener('pointercancel', reset);
        content.addEventListener('lostpointercapture', reset);
      }

      shouldIgnoreOutsideClose(target) {
        if (!target || !(target instanceof Element)) return true;
        return Boolean(
          target.closest(
            '.product-media-modal__toggle, .product-media-modal__nav, deferred-media, product-model, video, model-viewer, iframe, img.global-media-settings, .media'
          )
        );
      }

      isOutsideMediaClick(target) {
        if (!target || !(target instanceof Element)) return false;
        return (
          target === this ||
          target.classList.contains('product-media-modal__dialog') ||
          target.classList.contains('product-media-modal__content') ||
          target.getAttribute('role') === 'document'
        );
      }

      initNavigation() {
        if (this._navReady) return;
        this._navReady = true;

        this.prevButton = this.querySelector('.product-media-modal__nav--prev');
        this.nextButton = this.querySelector('.product-media-modal__nav--next');

        this.prevButton?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.stepMedia(-1);
        });
        this.nextButton?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.stepMedia(1);
        });
        this.prevButton?.addEventListener('pointerup', (event) => event.stopPropagation());
        this.nextButton?.addEventListener('pointerup', (event) => event.stopPropagation());

        if (!this._keydownBound) {
          this._keydownBound = true;
          this.addEventListener('keydown', this.onKeydown.bind(this));
        }
      }

      onKeydown(event) {
        if (!this.hasAttribute('open') || this.isZoomAnimating()) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.stepMedia(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.stepMedia(1);
        }
      }

      isZoomAnimating() {
        return (
          this.classList.contains('product-media-modal--opening') ||
          this.classList.contains('product-media-modal--closing')
        );
      }

      getNavigableMedia() {
        return [
          ...this.querySelectorAll(
            '.product-media-modal__content img.global-media-settings[data-media-id]'
          ),
        ];
      }

      updateNavigationState() {
        this.initNavigation();
        const items = this.getNavigableMedia();
        const navigable = items.length > 1;
        const animating = this.isZoomAnimating();

        if (this.prevButton) {
          this.prevButton.toggleAttribute('hidden', !navigable);
          this.prevButton.disabled = animating;
        }
        if (this.nextButton) {
          this.nextButton.toggleAttribute('hidden', !navigable);
          this.nextButton.disabled = animating;
        }
      }

      getSectionGallery() {
        if (!this.dataset.section) return null;
        const section = document.getElementById(`shopify-section-${this.dataset.section}`);
        return section?.querySelector('media-gallery') || null;
      }

      getGalleryOpener(mediaId) {
        const section = this.dataset.section
          ? document.getElementById(`shopify-section-${this.dataset.section}`)
          : null;
        return (
          section?.querySelector(
            `.product__media-item button.product__media-toggle[data-media-id="${CSS.escape(String(mediaId))}"]`
          ) || null
        );
      }

      syncGallery(mediaId) {
        const gallery = this.getSectionGallery();
        if (!gallery || !this.dataset.section) return;
        gallery.setActiveMedia(`${this.dataset.section}-${mediaId}`, false);
      }

      stepMedia(direction) {
        if (!this.hasAttribute('open') || this.isZoomAnimating()) return;
        const items = this.getNavigableMedia();
        if (items.length < 2) return;

        const currentId = this.openedBy?.getAttribute('data-media-id');
        let index = items.findIndex((el) => el.getAttribute('data-media-id') === currentId);
        if (index < 0) index = 0;

        const nextIndex = (index + direction + items.length) % items.length;
        const nextId = items[nextIndex].getAttribute('data-media-id');
        this.setActiveModalMedia(nextId);
      }

      setActiveModalMedia(mediaId) {
        if (!mediaId) return;

        const currentImage = this.getActiveImage(this.openedBy);
        if (currentImage) this.clearZoomStyles(currentImage);

        window.pauseAllMedia();
        this.showActiveMedia(mediaId);

        const opener = this.getGalleryOpener(mediaId);
        if (opener) this.openedBy = opener;

        this.syncGallery(mediaId);
        this.updateNavigationState();
      }

      get prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      getThumbnailImage(opener) {
        if (!opener) return null;
        const wrapper = opener.closest('.product__modal-opener');
        if (wrapper) {
          const img = wrapper.querySelector('.product__media img');
          if (img) return img;
        }
        const mediaId = opener.getAttribute('data-media-id');
        if (!mediaId) return null;
        const toggle = document.querySelector(
          `.product__media-item button.product__media-toggle[data-media-id="${CSS.escape(String(mediaId))}"]`
        );
        if (toggle) {
          return toggle.closest('.product__modal-opener')?.querySelector('.product__media img') || null;
        }
        return document.querySelector(`[data-media-id="${mediaId}"] .product__media img`);
      }

      listenForZoomTransitionEnd(image, callback) {
        let done = false;
        const complete = () => {
          if (done) return;
          done = true;
          image.removeEventListener('transitionend', onEnd);
          clearTimeout(this._zoomTimer);
          callback();
        };
        const onEnd = (event) => {
          if (event.target !== image || event.propertyName !== 'width') return;
          complete();
        };
        image.addEventListener('transitionend', onEnd);
        clearTimeout(this._zoomTimer);
        this._zoomTimer = setTimeout(complete, ZOOM_DURATION_MS + 120);
      }

      getActiveImage(opener) {
        const mediaId =
          opener?.getAttribute?.('data-media-id') || this.openedBy?.getAttribute('data-media-id');
        if (mediaId) {
          const image = this.querySelector(
            `.product-media-modal__content img.global-media-settings[data-media-id="${mediaId}"]`
          );
          if (image) return image;
        }
        return (
          this.querySelector('.product-media-modal__content img.global-media-settings.active') ||
          this.querySelector('.product-media-modal__content img.global-media-settings')
        );
      }

      clearZoomStyles(image) {
        if (!image) return;
        image.classList.remove('product-media-modal__image--zooming');
        image.style.transition = '';
        image.style.transform = '';
        image.style.opacity = '';
        image.style.willChange = '';
        image.style.position = '';
        image.style.left = '';
        image.style.top = '';
        image.style.width = '';
        image.style.height = '';
        image.style.margin = '';
        image.style.maxWidth = '';
        image.style.maxHeight = '';
        image.style.objectFit = '';
        image.style.zIndex = '';
      }

      getZoomTransition() {
        return `left ${ZOOM_DURATION_MS}ms ${ZOOM_EASING}, top ${ZOOM_DURATION_MS}ms ${ZOOM_EASING}, width ${ZOOM_DURATION_MS}ms ${ZOOM_EASING}, height ${ZOOM_DURATION_MS}ms ${ZOOM_EASING}, opacity ${ZOOM_DURATION_MS - 80}ms ease`;
      }

      /** Pin image to an exact viewport box (shared-element style zoom). */
      setZoomRect(image, rect, { animate = false } = {}) {
        image.classList.add('product-media-modal__image--zooming');
        image.style.willChange = 'left, top, width, height, opacity';
        image.style.position = 'fixed';
        image.style.left = `${rect.left}px`;
        image.style.top = `${rect.top}px`;
        image.style.width = `${rect.width}px`;
        image.style.height = `${rect.height}px`;
        image.style.margin = '0';
        image.style.maxWidth = 'none';
        image.style.maxHeight = 'none';
        image.style.transform = 'none';
        image.style.objectFit = 'contain';
        image.style.zIndex = '102';
        image.style.transition = animate ? this.getZoomTransition() : 'none';
      }

      playZoomIn(opener) {
        const image = this.getActiveImage(opener);
        const thumbEl = this.getThumbnailImage(opener);

        this.classList.remove('product-media-modal--closing');
        this.classList.add('product-media-modal--opening');
        this.updateNavigationState();

        if (this.prefersReducedMotion || !image || !thumbEl) {
          this.classList.add('product-media-modal--visible');
          this.classList.remove('product-media-modal--opening');
          this.updateNavigationState();
          return;
        }

        const finishZoomIn = () => {
          this.clearZoomStyles(image);
          this.classList.remove('product-media-modal--opening', 'product-media-modal--visible');
          this.updateNavigationState();
        };

        const startZoomIn = () => {
          const thumbRect = thumbEl.getBoundingClientRect();
          const targetRect = image.getBoundingClientRect();

          if (thumbRect.width < 1 || targetRect.width < 1) {
            this.classList.add('product-media-modal--visible');
            this.classList.remove('product-media-modal--opening');
            this.updateNavigationState();
            return;
          }

          this.setZoomRect(image, thumbRect);
          image.style.opacity = '1';

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.classList.add('product-media-modal--visible');
              this.setZoomRect(image, targetRect, { animate: true });
              image.style.opacity = '1';
            });
          });

          this.listenForZoomTransitionEnd(image, finishZoomIn);
        };

        requestAnimationFrame(() => {
          requestAnimationFrame(startZoomIn);
        });
      }

      playZoomOut(callback) {
        const image = this.getActiveImage(this.openedBy);
        const thumbEl = this.getThumbnailImage(this.openedBy);

        this.classList.remove('product-media-modal--opening', 'product-media-modal--visible');
        this.classList.add('product-media-modal--closing');
        this.updateNavigationState();

        const finish = () => {
          this.clearZoomStyles(image);
          this.classList.remove('product-media-modal--closing');
          callback();
        };

        if (this.prefersReducedMotion || !image || !thumbEl) {
          finish();
          return;
        }

        const thumbRect = thumbEl.getBoundingClientRect();
        const targetRect = image.getBoundingClientRect();

        if (thumbRect.width < 1 || targetRect.width < 1) {
          finish();
          return;
        }

        this.setZoomRect(image, targetRect);
        image.style.opacity = '1';

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.setZoomRect(image, thumbRect, { animate: true });
            image.style.opacity = '0';
          });
        });

        this.listenForZoomTransitionEnd(image, finish);
      }

      show(opener) {
        this.openedBy = opener;
        document.body.classList.add('overflow-hidden');
        this.setAttribute('open', '');
        this.updateNavigationState();
        trapFocus(this, this.querySelector('[role="dialog"]'));
        window.pauseAllMedia();
        this.showActiveMedia();
        this.playZoomIn(opener);
      }

      hide() {
        if (this.hasAttribute('closing')) return;
        this.setAttribute('closing', '');

        this.playZoomOut(() => {
          this.removeAttribute('closing');
          document.body.classList.remove('overflow-hidden');
          document.body.dispatchEvent(new CustomEvent('modalClosed'));
          this.removeAttribute('open');
          removeTrapFocus(this.openedBy);
          window.pauseAllMedia();
        });
      }

      showActiveMedia(mediaId) {
        const activeId = mediaId || this.openedBy?.getAttribute('data-media-id');
        if (!activeId) return;

        const content = this.querySelector('.product-media-modal__content');
        content?.querySelectorAll(':scope > [data-media-id]').forEach((element) => {
          element.classList.toggle('active', element.getAttribute('data-media-id') === activeId);
        });

        const activeMedia = content?.querySelector(`:scope > [data-media-id="${activeId}"]`);
        if (!activeMedia) return;

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

        const container = this.querySelector('[role="document"]');
        if (container && activeMedia.width) {
          container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
        }

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }
    }
  );
}
